import { RequiredValidator } from './../../../../../../shared/validators/form-validators';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { map, Observable } from 'rxjs';
import { Moedas } from 'src/app/domain/constants/moeda.constant';
import {
  ContactAccountDto,
  EAccountBankType,
} from 'src/app/domain/models/dto/contact-account.dto';
import { CountryDto } from 'src/app/domain/models/dto/country.dto';
import { CreateOrderInboundDto } from 'src/app/domain/models/dto/create-order.dto';
import { AuthenticationService } from 'src/app/shared/services';
import { BankAccountService } from 'src/app/shared/services/bank-account.service';
import { CountriesService } from 'src/app/shared/services/countries.service';
import { CountryValidationRules } from 'src/app/domain/constants/country-validation.constant';
import { ResponseObjectModel } from 'src/app/domain/models/response-object.model';

@Component({
  selector: 'app-sender-selection',
  templateUrl: './sender-selection.component.html',
  styleUrls: ['./sender-selection.component.scss'],
})
export class SenderSelectionComponent implements OnInit {
  @Input('data') data?: CreateOrderInboundDto;
  @Input('loading') loading?: boolean;
  @Input('country') country?: CountryDto;
  @Input('warrenGlobalOnly') warrenGlobalOnly?: boolean;
  @Output('countrySave') countrySave?: EventEmitter<any> = new EventEmitter();
  @Output('goBack') goBack: EventEmitter<any> = new EventEmitter();
  @Output('closeModal') closeModal: EventEmitter<any> = new EventEmitter();
  @Output('senderSave') senderSave: EventEmitter<CreateOrderInboundDto> =
    new EventEmitter();

  _currency: string;
  @Input() set currency(value: string) {
    this._currency = value;
    if (this.form) {
      this.form.controls['currencyDescription'].setValue(this._currency);
    }

    if (this._currency) {
      this.loadUserContacts();
    }
  }
  countryValidationRules = CountryValidationRules;
  form!: FormGroup;
  countries$: Observable<CountryDto[]>;
  isMobile = false;
  userContacts: ContactAccountDto[];
  contactType: string = 'SenderAccount';
  isNewSender = false;
  allCountries: CountryDto[] = [];

  moedas = Moedas;

  get companyId() {
    return +this.accountService.getCompanyId();
  }

  constructor(
    formBuilder: FormBuilder,
    private readonly countriesService: CountriesService,
    private readonly accountService: AuthenticationService,
    private readonly bankAccountService: BankAccountService
  ) {
    this.form = formBuilder.group({
      currencyDescription: ['', Validators.required],
      nameSender: ['', RequiredValidator],
      countrySenderId: [this.country, Validators.required],
      amount: ['', [Validators.required, Validators.min(1)]],
      emailSender: ['', [Validators.email, Validators.required]],
    });
  }
  ngOnInit(): void {
    this.isMobile = window.innerWidth <= 820;
    this.form.controls['currencyDescription'].setValue(this._currency);

    this.countries$ = this.countriesService.get().pipe(map((res) => res.data));
    this.countries$.subscribe((countries) => {
      this.allCountries = countries;

      if (this.warrenGlobalOnly) {
        this.populateFormWithWarrenGlobalOnly();
      } else if (this.data) {
        this.populateFormWithExistingData();
      }
    });
  }

  private populateFormWithWarrenGlobalOnly(): void {
    this.isNewSender = false;

    const contact: any = {
      country: {
        id: 13,
        description: 'Estados Unidos',
        code: '2496',
      },
      currency: {
        id: 1,
        description: 'USD',
        code: 220,
        ptaxes: [],
        beneficiaries: [],
        beneficiaryBankChannels: [],
        intermediaryBankChannels: [],
      },
      bank: '',
      isDefault: false,
      type: EAccountBankType.SenderAccount,
      senderReceiverName: 'Interactive Brokers LLC',
      email: 'warrencambio@warren.com.br',
    };

    this.form.patchValue({
      nameSender: contact.senderReceiverName,
      emailSender: contact.email,
      countrySenderId: contact.country,
      currencyDescription: contact.currency.description,
    });

    Object.keys(this.form.controls).forEach((key) => {
      if (key === 'amount') {
        this.form.controls[key].enable();
      } else {
        this.form.controls[key].disable();
      }
    });
  }

  private populateFormWithExistingData(): void {
    this.isNewSender = false;

    const matchedCountry = this.allCountries.find(
      (c) => c.id === this.data?.countrySenderId
    );

    this.form.patchValue({
      ...this.data,
      countrySenderId: matchedCountry || '',
    });

    Object.keys(this.form.controls).forEach((key) => {
      if (key === 'amount') {
        this.form.controls[key].enable();
      } else {
        this.form.controls[key].disable();
      }
    });
  }

  close() {
    this.closeModal.emit(true);
  }

  get isFormVisible(): boolean {
    return this.isNewSender || !!this.form.get('nameSender')?.value;
  }

  private loadUserContacts(): void {
    this.bankAccountService
      .getAllSendersandReceivers(this.contactType, this._currency)
      .subscribe({
        next: (res) =>
          (this.userContacts = res.data.sort((a, b) =>
            a.senderReceiverName?.localeCompare(b.senderReceiverName)
          )),
      });
  }

  getCurrencyDescription(currency: any): string {
    const moeda = this.moedas.find((m) => m.id === currency.id);
    return moeda?.description || currency.description;
  }

  onNewSender() {
    this.isNewSender = true;
    this.form.reset();

    Object.keys(this.form.controls).forEach((key) => {
      this.form.controls[key].enable();
    });

    this.form.controls['currencyDescription'].setValue(this._currency);
  }

  submit() {
    const createOrderDto = this.createOrderDto();

    if (this.isNewSender) {
      const country = this.form.get('countrySenderId')?.value;
      const contactAccount: ContactAccountDto = {
        senderReceiverName: this.form.get('nameSender')?.value,
        email: this.form.get('emailSender')?.value,
        country: country,
        currency: this.moedas.find((m) => m.value === this._currency),
        type: EAccountBankType.SenderAccount,
      };

      this.bankAccountService.addBankAccount(contactAccount).subscribe({
        next: (createdAccount: ResponseObjectModel<ContactAccountDto>) => {
          this.form.patchValue({
            nameSender: createdAccount?.data?.senderReceiverName,
            emailSender: createdAccount?.data?.email,
            countrySenderId: createdAccount?.data?.country,
            currencyDescription: createdAccount?.data?.currency?.description,
          });

          const updatedOrderDto: CreateOrderInboundDto = this.createOrderDto();
          this.senderSave.emit(updatedOrderDto);
        },
      });
    } else {
      this.senderSave.emit(createOrderDto);
    }
  }

  back() {
    this.goBack.emit(true);
  }

  private createOrderDto(): CreateOrderInboundDto {
    const createOrderDto = new CreateOrderInboundDto();
    const values = this.form.getRawValue();

    const country = values.countrySenderId;
    values.countrySenderId = country?.id;
    this.countrySave.emit(country);

    Object.assign(createOrderDto, values);
    return createOrderDto;
  }

  compareCountry(obj1: CountryDto, obj2: CountryDto): boolean {
    return obj1 && obj2 ? obj1.id === obj2.id : obj1 === obj2;
  }

  onContactSelect(contact: ContactAccountDto) {
    this.isNewSender = false;

    this.form.reset();

    this.form.patchValue({
      nameSender: contact.senderReceiverName,
      emailSender: contact.email,
      countrySenderId: contact.country,
      currencyDescription: contact.currency?.description,
    });

    Object.keys(this.form.controls).forEach((key) => {
      if (key === 'amount') {
        this.form.controls[key].enable();
      } else {
        this.form.controls[key].disable();
      }
    });
  }
}
